import React, { FunctionComponent, useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Row, Col, Card, Button, Form , InputGroup, Alert } from 'react-bootstrap'
import BootstrapTable from 'react-bootstrap-table-next'
import ToolkitProvider from 'react-bootstrap-table2-toolkit'
import CircularProgress from '@material-ui/core/CircularProgress'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import CarePlanRow from './CarePlanRow'
import Template from './Template'
import { formatUTCDate } from '../../../utils/stringTools'
import RootState from '../../../types/RootState'
import CarePlanGrid from '../../../types/CarePlanGrid'
import { addUserActivity } from '../../../redux/actions/authentication'
import { getCarePlanGrid } from '../../../redux/actions/carePlan'
import { pdf } from '@react-pdf/renderer'
import { saveAs } from 'file-saver'
import CryptoJS from 'crypto-js'
import { capitalize } from 'lodash'

import './index.css'

interface Props {
  height: number;
}

const expandRowRenderer = (row: CarePlanGrid) => {
  return (
    <div>
      <div className='care-plan-title'>Care Plan Details</div>
      {
        row.problem.map((problem, index) => (
          <CarePlanRow
            key={`${row.carePlanProviderSignatureId}__${index}`}
            carePlanProviderSignatureId={row.carePlanProviderSignatureId}
            carePlanProblem={problem}
          />
        ))
      }
    </div>
  )
}

const expandHeaderColumnRenderer = () => <div />

const expandColumnRenderer = ({ expanded }: any) =>  expanded ? <FontAwesomeIcon icon={['fas', 'chevron-up']} className='expandedIcon' /> : <FontAwesomeIcon icon={['fas', 'chevron-down']} className='expandedIcon' />

const filterCarePlans = (carePlans: Array<CarePlanGrid>, state: any) => {
  let filteredCarePlans = carePlans
  if (state.memberFullName !== '') {
    filteredCarePlans = filteredCarePlans.filter((carePlan: CarePlanGrid) => carePlan.memberFullName.toLowerCase().indexOf(state.memberFullName.toLowerCase()) !== -1)
  }
  if (state.providerName !== '') {
    filteredCarePlans = filteredCarePlans.filter((carePlan: CarePlanGrid) => carePlan.providerName.toLowerCase().indexOf(state.providerName.toLowerCase()) !== -1)
  }
  return filteredCarePlans
}

const renderDownloadButton = (cellContent, row, setShowLoadingReport, handleDownloadPdf) => (
  <Button  variant='info' onClick={() => {
    setShowLoadingReport(true)
    setTimeout(() => {
      handleDownloadPdf(row)
    }, 1500)
  }}>Download</Button>
)

const CarePlan: FunctionComponent<Props> = ({ height }: Props) => {
  const dispatch = useDispatch()
  const [expanded, setExpanded] = useState<Array<number>>([])
  const { carePlan, loading, selectedOrganization, user } = useSelector((state: RootState) => ({
    carePlan: state.carePlan.carePlanGrid.data,
    loading: state.carePlan.carePlanGrid.loading,
    selectedOrganization: state.authentication.selectedOrganization,
    user: state.authentication.user.data,
  }))

  const [carePlanFilters, setCarePlanFilters] = useState({
    memberFullName: '',
    providerName: ''
  })

  // Download PDF functionality
  const [showLoadingReport, setShowLoadingReport] = useState(false)
  const [showDownloadError, setShowDownloadError] = useState(false)
  const handleDownloadPdf = async (carePlan: CarePlanGrid) => {
    const emailKey = 'lakelandproviderportalinsertuserencryption'
    const email = user.isLakeland ? { eid: CryptoJS.AES.encrypt(user.email, emailKey).toString() } : JSON.parse(localStorage.getItem('p_sid')|| '{}')
    const token = localStorage.getItem('token') !== null && localStorage.getItem('token') !== 'undefined' ? JSON.parse(localStorage.getItem('token')|| '') : ''
    try {
      setShowDownloadError(false)
      const blob = await pdf((
        <Template
          carePlan={carePlan}
        />
      )).toBlob()
      saveAs(blob, `Care Plan Summary_${carePlan.memberId}_${formatUTCDate(new Date())}.pdf`)
      setShowLoadingReport(false)

      dispatch(addUserActivity({
        eid: email.eid,
        activity: 'Download Care Plan',
        memberId: carePlan.memberId
      }, token))
    } catch (error) {
      // SHOW ALERT WHEN DOWNLOAD FAILS
      setShowDownloadError(true)
      setShowLoadingReport(false)
    }
  }

  useEffect(() => {
    const key = 'lakelandproviderportalinsertuserencryption'
    const token = localStorage.getItem('token') !== null && localStorage.getItem('token') !== 'undefined' ? JSON.parse(localStorage.getItem('token')|| '') : ''
    const email = user.isLakeland ? { eid: CryptoJS.AES.encrypt(user.email, key).toString() } : JSON.parse(localStorage.getItem('p_sid')|| '{}')

    dispatch(getCarePlanGrid(selectedOrganization.organization_id, email.eid, token))
  }, [selectedOrganization]) // eslint-disable-line react-hooks/exhaustive-deps

  const data = loading === false ? (<div>No Care Plans available</div>) : (
    <div className='organization-circular-progress'>
      <CircularProgress size={40} />
      <span>Loading data</span>
    </div>
  )

  const columns = [{
    dataField: 'memberFullName',
    text: 'Name',
    sort: true,
  }, {
    dataField: 'memberId',
    text: 'Member ID',
    sort: true,
  }, {
    dataField: 'memberDateOfBirth',
    text: 'DOB',
    sort: true,
    formatter: (cellContent) => ( cellContent && formatUTCDate(cellContent) ) || '',
  }, {
    dataField: 'memberGender',
    text: 'Gender',
    sort: true,
    formatter: (cellContent) => capitalize(cellContent)
  }, {
    dataField: 'providerName',
    text: 'Location',
    sort: true,
  }, {
    dataField: 'carePlanDate',
    text: 'Care Plan Date',
    sort: true,
    formatter: (cellContent) => ( cellContent && formatUTCDate(cellContent) ) || '',
  }, {
    dataField: 'careManagerFullName',
    text: 'Care Manager',
    sort: true,
  }, {
    dataField: 'nurseFullName',
    text: 'RN Care Manager',
    sort: true,
  }, {
    dataField: 'acknowledgeDate',
    text: 'Acknowledge Date',
    sort: true,
    formatter: (cellContent) => ( cellContent && formatUTCDate(cellContent) ) || '',
  }, {
    dataField: 'signedFullName',
    text: 'Signer Name',
    sort: true,
  }, {
    dataField: 'download',
    text: 'Actions',
    formatter: (cellContent, row) => renderDownloadButton(cellContent, row, setShowLoadingReport, handleDownloadPdf)
  }]

  const handleChangeFilter = (val: any, key: string) => {
    setCarePlanFilters({
      ...carePlanFilters,
      [key]: val
    })
  }

  const handleOnExpand = (row, isExpand) => {
    const emailKey = 'lakelandproviderportalinsertuserencryption'
    const email = user.isLakeland ? { eid: CryptoJS.AES.encrypt(user.email, emailKey).toString() } : JSON.parse(localStorage.getItem('p_sid')|| '{}')
    const token = localStorage.getItem('token') !== null && localStorage.getItem('token') !== 'undefined' ? JSON.parse(localStorage.getItem('token')|| '') : ''

    if (isExpand) {
      setExpanded([...expanded, row.carePlanProviderSignatureId])
    } else {
      setExpanded(expanded.filter(x => x !== row.carePlanProviderSignatureId))
    }

    const userActivity = {
      eid: email.eid,
      activity: 'View Care Plan Details',
      carePlanProviderSignatureId: row.carePlanProviderSignatureId,
    }
    dispatch(addUserActivity(userActivity, token))
  }

  const expandRow = {
    onlyOneExpanding: true,
    renderer: (row) => expandRowRenderer(row),
    showExpandColumn: true,
    expandByColumnOnly: true,
    expandHeaderColumnRenderer: expandHeaderColumnRenderer,
    expandColumnRenderer: expandColumnRenderer,
    onExpand: handleOnExpand,
    expanded,
  }

  return (
    <div style={{ height:height-353 }}> 
      <Card className='filter-container'>
        <Card.Body>
          <Row>
            <Col sm={6} md={3}>
              <Form.Label>Member Name</Form.Label>
              <InputGroup className='mb-3 rate-search-filter' style={{marginBottom:0 }}>
                <InputGroup.Append>
                  <Button variant='outline-secondary' className='search-btn'><FontAwesomeIcon icon={['fas', 'search']} /></Button>
                </InputGroup.Append>
                <Form.Control 
                  type='text' 
                  placeholder='Quick Search' 
                  value={carePlanFilters.memberFullName} 
                  onChange={(event) => handleChangeFilter(event.target.value, 'memberFullName')}
                />
              </InputGroup>
            </Col>
            <Col sm={6} md={3}>
              <Form.Label>Location</Form.Label>
              <InputGroup className='mb-3 rate-search-filter' style={{marginBottom:0}}>
                <InputGroup.Append>
                  <Button variant='outline-secondary' className='search-btn'><FontAwesomeIcon icon={['fas', 'search']} /></Button>
                </InputGroup.Append>
                <Form.Control 
                  type='text' 
                  placeholder='Quick Search' 
                  value={carePlanFilters.providerName}
                  onChange={(event) => handleChangeFilter(event.target.value, 'providerName')}
                />
              </InputGroup>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      {
        showLoadingReport && (
          <Alert variant='light'>
            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0px'}}>
              <FontAwesomeIcon icon={['fas', 'spinner']} size='2x' />
              <span>Downloading data</span>
            </div>
          </Alert>
        )
      }
      {
        showDownloadError && (
          <Alert variant='danger' onClose={() => setShowDownloadError(false)} dismissible>
            Download failed. Please try again.
          </Alert>
        )
      }
      <div style={{height: '100%'}}>
        <ToolkitProvider
          keyField='carePlanProviderSignatureId'
          data={filterCarePlans(carePlan, carePlanFilters)}
          columns={columns}
          search
        >
          {
            props => (
              <div className='care-plan-table' style={{width: '100%'}}>
                <BootstrapTable
                  id='carePlanTable'
                  bordered={false}
                  striped
                  condensed
                  bootstrap4
                  noDataIndication={() => (data)}
                  expandRow={expandRow}
                  { ...props.baseProps }
                />
              </div>
            )
          }
        </ToolkitProvider>
      </div>
    </div>
  )
}

export default CarePlan